import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ContactsService } from 'app/store/services/contacts.service';
import { IContact, IContactActivity } from 'app/store/models/contact.model';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { Store } from '@ngrx/store';
import { IReducers } from 'app/store/reducers';
import { openDialog } from 'app/store/actions/page.action';
import { ContactConversationComponent } from '../contact-conversation/contact-conversation.component';
import { ApiDataSource } from 'app/datasources/api.datasrouce';
import { HttpClient } from '@angular/common/http';
import { DataSourceParams, ETableColumnType, ITableColumn } from 'app/store/models/table.model';
import { MessageDetailComponent } from './message-detail/message-detail.component';
import { environment } from '../../../environments/environment';
import { SessionService } from '../../store/services/session.service';
import { IUserSessionData } from '../../store/models/user.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatRadioChange } from '@angular/material/radio';
import { TableComponent } from '../../shared/ui/table/table.component';

@Component({
  selector: 'app-contact-activity',
  templateUrl: './contact-activity.component.html',
  styleUrls: ['./contact-activity.component.scss'],
})
export class ContactActivityComponent implements OnInit, OnDestroy {
  @Input() contactId: number;

  @ViewChild('tableComponent') tableComponent: TableComponent;

  activityType = '1';

  contactInfo: IContact;
  isContactDuplicate: boolean;

  dataSource: ApiDataSource<IContactActivity>;
  dataSourceParams: DataSourceParams = {
    page: 1,
    pageSize: 10,
    sortAttr: 'activity_date',
    sortDir: 'desc',
    filters: {},
  };

  tableColumns: ITableColumn<IContactActivity>[] = [];

  defaultTableColumns: ITableColumn<IContactActivity>[] = [
    {
      id: 'activity_date',
      title: 'Date',
      isDateColumn: true,
    },
    {
      id: 'activity_type',
      title: 'Type',
    },
    {
      id: 'message_details',
      title: 'Details',
      type: ETableColumnType.COMPONENT,
      componentProperties: {
        component: MessageDetailComponent,
        mapData: {
          messageDetail: {
            instance: 'column',
            target: 'activity_message',
          },
          messageType: {
            instance: 'column',
            target: 'activity_type',
          },
          messageContent: {
            instance: 'column',
            target: 'activity_content',
          },
          activityId: {
            instance: 'column',
            target: 'activity_id',
          },
          contactInfo: {
            instance: 'function',
            target: () => this.contactInfo,
          },
        },
      },
    },
  ];

  contactReplayAction: boolean;

  emailContactButtonProperties: Button = {
    text: 'Download Contact Activity',
    color: ButtonColor.BLUE,
    size: ButtonSize.NORMAL,
    onClick: () => {
      const accessToken = localStorage.getItem('token');

      if (!accessToken) {
        return;
      }

      const token = JSON.parse(accessToken);

      if (!token.access_token) {
        return;
      }
      window.open(
        this.contactsService.getDownloadContactActivityUrl(
          this.contactId,
          token.access_token,
        ),
        '_blank',
      );
    },
  };

  viewConversationButtonProperties: Button = {
    text: 'View Conversation',
    size: ButtonSize.SMALL,
    color: ButtonColor.ORANGE,
    onClick: () =>
      this.store.dispatch(
        openDialog({
          dialogData: {
            title: `Conversation with ${this.contactInfo.firstname} ${this.contactInfo.lastname}`,
            content: ContactConversationComponent,
            componentData: {
              messageId: this.contactInfo.message_id,
            },
            dialogMinWidth: '1000px',
            canClose: true,
          },
        }),
      ),
  };

  sessionData: IUserSessionData;

  private readonly destroy$ = new Subject<boolean>();

  constructor(
    private contactsService: ContactsService,
    private store: Store<IReducers>,
    private http: HttpClient,
    private readonly sessionService: SessionService,
  ) {
  }

  ngOnInit() {
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(sessionData => {
        this.sessionData = sessionData;

        this.contactsService
          .viewContact(this.contactId, true)
          .subscribe(contact => {
            this.contactInfo = contact;
            this.contactReplayAction = !!this.contactInfo.reply_action_id;
            this.tableColumns = this.defaultTableColumns;

            this.dataSource = new ApiDataSource<any>(
              this.http,
              environment.apiUrl + `/v1/contacts/${this.contactId}/history`,
            );
          });
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  changeActivitySource($event: MatRadioChange) {
    this.activityType = $event.value;
    if ($event.value === '2') {
      this.tableColumns = [
        {
          id: 'message_date',
          title: 'Date',
          isDateColumn: true,
        },
        {
          id: 'message_type',
          title: 'Type',
        },
        {
          id: 'message_details',
          title: 'Details',
          type: ETableColumnType.COMPONENT,
          componentProperties: {
            component: MessageDetailComponent,
            mapData: {
              messageDetail: {
                instance: 'column',
                target: 'message_details',
              },
              messageType: {
                instance: 'column',
                target: 'message_type',
              },
              messageContent: {
                instance: 'column',
                target: 'message_details',
              },
              activityId: {
                instance: 'column',
                target: 'activity_id',
              },
              contactInfo: {
                instance: 'function',
                target: () => this.contactInfo,
              },
            },
          },
        },
      ];
      this.dataSource = new ApiDataSource<any>(
        this.http,
        environment.apiUrl + `/v1/contacts/${this.contactId}/activity`,
      );

      this.tableComponent.forceRefresh();

      return;
    }

    this.tableColumns = this.defaultTableColumns;

    this.dataSource = new ApiDataSource<any>(
      this.http,
      environment.apiUrl + `/v1/contacts/${this.contactId}/history`,
    );
    this.tableComponent.forceRefresh();
  }
}
